import {
  trackToolExperience,
  deviceLayout,
} from "../../../features/toolExperience/telemetry";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../Modal/Modal";
import { Card, FormField, FormModal, Input } from "../../ui";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../hooks/useToast";
import {
  useClusteringProject,
  useCreateBatch,
  useEstimateBatch,
} from "../../../hooks/useClustering";
import {
  DEFAULT_BATCH_CONFIG,
  type BatchConfig,
  type EstimateResult,
} from "../../../types/clustering";
import { canMutate, formatNumber, formatUsd } from "../shared";
import { KeywordsInput } from "./KeywordsInput";
import { BatchConfigForm } from "./BatchConfigForm";
import styles from "../Clustering.module.css";

interface CreateBatchViewProps {
  projectId: string;
}

/** Step indices for the 2-step create flow. */
const CONFIGURE_STEP = 0;
const ESTIMATE_STEP = 1;

function parseKeywords(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * Batch creation as a single 2-step modal.
 *
 * Step 1 ("Configure") collects the batch name, keywords and SERP config.
 * Step 2 ("Review estimate") shows the cost quote that used to live in a
 * separate `EstimateModal` opened on top of this view. Folding it into a step
 * removes the modal-opens-modal anti-pattern and, more importantly, makes the
 * quote structurally unskippable: the only control that creates a batch is
 * step 2's Submit button, and it only enables once a quote for the *current*
 * configuration has come back.
 */
export function CreateBatchView({ projectId }: CreateBatchViewProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const { data: project } = useClusteringProject(projectId);
  const { mutateAsync: requestEstimate } = useEstimateBatch();
  const createMut = useCreateBatch();

  const [name, setName] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [config, setConfig] = useState<BatchConfig>(DEFAULT_BATCH_CONFIG);
  const [estimate, setEstimate] = useState<EstimateResult | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(CONFIGURE_STEP);
  const [stepError, setStepError] = useState<string | null>(null);

  const keywords = parseKeywords(keywordsText);

  // Every entry into the estimate step re-quotes from scratch. The effect is
  // deliberately keyed on `currentStep` alone: it therefore closes over the
  // `keywords`/`config` of the render in which the step became ESTIMATE_STEP,
  // i.e. exactly the configuration the user just finished editing. Adding
  // `keywords`/`config` to the dependency array would re-fire on every
  // keystroke (and loop, since `keywords` is a fresh array each render), while
  // leaving them out cannot go stale because step 1's fields are unmounted
  // while step 2 is showing. Going Back and forward again flips the dependency
  // 1 → 0 → 1, so the quote is always re-fetched rather than reused.
  useEffect(() => {
    if (currentStep !== ESTIMATE_STEP) return;

    let cancelled = false;
    setEstimate(null);
    setEstimateError(null);

    requestEstimate({ keywords, config })
      .then((result) => {
        if (!cancelled) setEstimate(result);
      })
      .catch((err) => {
        if (!cancelled) setEstimateError((err as Error).message);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const close = () => navigate(`/clustering/${projectId}`);

  if (!canMutate(user)) {
    return (
      <Modal isOpen onClose={close} title="Read-only access" size="small">
        <p className={styles.mutedText}>
          Your role cannot submit new clustering batches.
        </p>
      </Modal>
    );
  }

  const handleStepValidate = (step: number): boolean => {
    if (step === CONFIGURE_STEP && keywords.length === 0) {
      setStepError(
        "Add at least one keyword before reviewing the cost estimate.",
      );
      return false;
    }
    setStepError(null);
    return true;
  };

  const handleStepChange = (next: number) => {
    setStepError(null);
    // Drop any previous quote in the *same* update as the step change, so the
    // first render of the estimate step can never briefly paint (or accept a
    // Submit against) a cost computed for an older configuration. The effect
    // below clears them again, but only after paint — this is the synchronous
    // half of the guarantee.
    setEstimate(null);
    setEstimateError(null);
    setCurrentStep(next);
  };

  const handleConfirm = async () => {
    // Billing guard: creation is only ever reachable from the estimate step,
    // and only once a quote for the current configuration has loaded. This
    // also neutralises any implicit form submission from step 1. The
    // createMut.isPending check closes a re-entrancy window: a double-click
    // within the same render frame (before the button's disabled attribute
    // updates) could otherwise fire two create-batch mutations and spend
    // credits twice.
    if (currentStep !== ESTIMATE_STEP || !estimate || createMut.isPending)
      return;

    try {
      const batch = await createMut.mutateAsync({
        project_id: projectId,
        name: name.trim() || null,
        keywords,
        config,
      });
      trackToolExperience("task_submitted", {
        toolId: "clustering",
        source: "page",
        layout: deviceLayout(),
      });
      toast.success("Batch queued", "Streaming live progress now.");
      navigate(`/clustering/${projectId}/batches/${batch.id}`);
    } catch (err) {
      toast.error("Batch creation failed", (err as Error).message);
    }
  };

  // True from the moment step 2 is entered until the quote resolves one way or
  // the other, so there is no frame in which step 2 shows neither a spinner,
  // an error, nor a cost.
  const isEstimating =
    currentStep === ESTIMATE_STEP && !estimate && !estimateError;

  // Step 1's fields are unmounted while a quote is in flight, so the only
  // reason to lock them is an in-flight batch creation.
  const fieldsDisabled = createMut.isPending;

  const configureStep = (
    <>
      <p className={styles.mutedText}>
        Paste or upload your keywords and tune the SERP parameters. You will see
        a cost estimate before any credits are spent.
      </p>

      <FormField label="Batch name (optional)" htmlFor="batch-name">
        <Input
          id="batch-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Q2 blog refresh"
          fullWidth
        />
      </FormField>

      <KeywordsInput
        value={keywordsText}
        onChange={(next) => {
          setKeywordsText(next);
          if (stepError) setStepError(null);
        }}
        disabled={fieldsDisabled}
      />

      <BatchConfigForm
        value={config}
        onChange={setConfig}
        disabled={fieldsDisabled}
      />

      {stepError && (
        <div className={styles.error} role="alert">
          {stepError}
        </div>
      )}
    </>
  );

  const estimateStep = (
    <>
      <h3 className={styles.estimateHeading}>Batch cost estimate</h3>

      {estimateError && (
        <div className={styles.error} role="alert">
          {estimateError}
        </div>
      )}

      {isEstimating && <div className={styles.loading}>Estimating…</div>}

      {estimate && (
        <Card variant="outlined" padding="md">
          <dl className={styles.estimateList}>
            <dt className={styles.estimateTerm}>Keywords submitted</dt>
            <dd className={styles.estimateValue}>
              {formatNumber(estimate.keywords_submitted)}
            </dd>

            <dt className={styles.estimateTerm}>Credits required</dt>
            <dd
              className={`${styles.estimateValue} ${styles.estimateValueStrong}`}
            >
              {formatNumber(estimate.credits_required)}
            </dd>

            <dt className={styles.estimateTerm}>USD estimate</dt>
            <dd
              className={`${styles.estimateValue} ${styles.estimateValueStrong}`}
            >
              {formatUsd(estimate.usd_estimate)}
            </dd>

            <dt className={styles.estimateTerm}>Pricing date</dt>
            <dd className={styles.estimateValue}>{estimate.fetched_date}</dd>
          </dl>
        </Card>
      )}

      <p className={styles.estimateNote}>
        Cancelling mid-run stops further spend.
      </p>
    </>
  );

  return (
    <FormModal
      isOpen
      onClose={close}
      title={`New batch${project ? ` in ${project.name}` : ""}`}
      size="large"
      steps={[
        { label: "Configure", content: configureStep },
        { label: "Review estimate", content: estimateStep },
      ]}
      currentStep={currentStep}
      onStepChange={handleStepChange}
      onStepValidate={handleStepValidate}
      onSubmit={() => {
        void handleConfirm();
      }}
      submitLabel="Run batch"
      isSubmitting={createMut.isPending}
      submitDisabled={!estimate || isEstimating}
    >
      {/* Body is supplied by `steps`; FormModal ignores children when stepped. */}
      {null}
    </FormModal>
  );
}
